import { useQuery } from '@tanstack/react-query';

import { getWorkspaceFileHistory } from '@/lib/api/generated/api';
import type { FileChangeEventView } from '@/lib/api/generated/model';
import { fileKeys } from '../queryKeys';

/**
 * What a summary widget shows before it starts counting instead.
 *
 * Small on purpose. This is the "has anything moved lately" glance, not the
 * history page — that already exists per file, with paging, and this must not
 * grow into a second one.
 */
export const CHANGE_FEED_SIZE = 5;

export interface WorkspaceChangeFeed {
  events: FileChangeEventView[];
  /** Everything the log holds, not just what was read. */
  total: number;
  isPending: boolean;
  /** True when the read did not come back. Never conflated with "nothing yet". */
  loadFailed: boolean;
}

/**
 * The workspace's file changes, newest first.
 *
 * One read of `/files/history` with no `path`, which the service answers with
 * the whole log rather than one file's. That form did not exist when Overview
 * was specified — the endpoint required a path, so a workspace-wide feed would
 * have cost one request per directory and still could not have named an actor.
 * It now costs one request and every event carries one.
 *
 * Server-sorted; this hook never re-sorts. The service orders by
 * `(occurred_at desc, id desc)`, the same total order a single file's history
 * uses, so the feed and a file's own page cannot disagree about which of two
 * events came first.
 *
 * A non-200 resolves to `loadFailed` rather than throwing, matching the
 * asymmetry the other file queries keep: a read that failed is not the same as
 * a workspace where nothing has happened, and a widget that showed "no changes
 * yet" for an unreachable service would be lying.
 */
export function useWorkspaceChangeFeed(
  workspaceId: string,
  limit: number = CHANGE_FEED_SIZE,
): WorkspaceChangeFeed {
  const query = useQuery({
    queryKey: fileKeys.workspaceHistory(workspaceId),
    queryFn: async () => {
      const response = await getWorkspaceFileHistory(workspaceId, { limit });
      return response.status === 200 ? response.data : null;
    },
    retry: false,
  });

  return {
    events: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isPending: query.isPending,
    loadFailed: !query.isPending && (query.isError || query.data === null),
  };
}
